/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {Box} from '@material-ui/core';
import InputLabel from '@material-ui/core/InputLabel';
import Popover from '@material-ui/core/Popover';
import {makeStyles} from '@material-ui/core/styles';
import ClockIcon from '@material-ui/icons/AccessTime';
import classnames from 'classnames';
import {
  addMonths,
  isSameDay,
  setYear
} from 'date-fns';
import {Moment} from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  formatIfValid,
  isValid,
  isValidDateTime,
  resolveDateInput,
  twelveHourTime,
  VALID_MANUAL_DATE_FORMATS
} from '@ion/core/src/utils/DateUtil';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {DateOutlinedIcon} from '../../../src/icons/DateOutlinedIcon';
import {NotificationCalendarIcon} from '../../../src/icons/NotificationCalendarIcon';
import {TimeOutlinedIcon} from '../../../src/icons/TimeOutlinedIcon';
import {TertiaryButton} from '../../components/buttons/TertiaryButton';
import TextField from '../../components/textfield/TextField';
import {dlsAlt, dlsColor, dlsTypography, dlsZIndex} from '../../theme/theme';
import RenderDatePicker from './RenderDatePicker';

export interface DatePickerTextConfig {
  useDatePickerTextField?: boolean;
  validDateTimeFormats?: string[];
  validTimeFormats?: string[];
  validDateFormats?: string[];
  allowManualEntry?: boolean;
  allowManualDateEntry?: boolean;
};

interface StyleProps extends DatePickerTextConfig {
  fieldLabel?: string;
  overridePopupStyle?: GenericObject;
  externalClasses?: GenericObject;
};

export interface CurrentDayProps {
  isNow?: boolean;
  isToday?: boolean;
};

// defines the prop definitions for the component
export interface DatePickerProps extends StyleProps, CurrentDayProps {
  toggle?: () => void;
  onChange?: (date: Date | Moment | string, required: boolean) => void;
  onClose?: () => void;
  closeOnClickOutside?: boolean;
  wrapperClassName?: string;
  value?: Date;
  isDate?: boolean;
  isTime?: boolean;
  isDateTime?: boolean;
  defaultValue?: Date | string;
  placeholder?: string;
  hasError?: boolean;
  errorText?: string;
  required?: boolean;
  disabled?: boolean;
  manualInput?: boolean;
  isClock?: boolean;
  isDatePicker?: boolean;
  customError?: boolean;
  validDateTimeFormats?: string[];
  validTimeFormats?: string[];
  hideClearButton?: boolean;
  alternateInputStyle?: boolean;
  popoverOffset?: number | string;
  includeContainerStyles?: boolean;
};

type DatePickerErrorState = {
  hasError: boolean;
  message: string
};

const toggleStyle = (manual: string, auto: string, property?: string): any =>
  ({allowManualEntry, overridePopupStyle}: StyleProps): string => (overridePopupStyle && property)
    ? overridePopupStyle?.[property] : allowManualEntry ? manual : auto;
;

const useStyles = makeStyles(theme => ({
  popup : {
    margin                         : toggleStyle('2px 0', '16px'),
    padding                        : toggleStyle('12px', 'auto'),
    position                       : toggleStyle('absolute', 'unset', 'position'),
    backgroundColor                : toggleStyle(dlsColor('white'), ''),
    width                          : toggleStyle('auto', '312px'),
    boxShadow                      : toggleStyle('0px 2px 4px 3px rgb(0 0 0 / 20%)', 'unset'),
    right                          : ({popoverOffset}: DatePickerProps) => popoverOffset || 0,
    [theme.breakpoints.down('sm')] : {
      position : toggleStyle('relative', 'unset')
    }
  },
  placeholder : {
    position        : 'relative',
    width           : '130px',
    top             : ({fieldLabel}: {fieldLabel: StyleProps}) => fieldLabel ? '37px' : '20px',
    paddingLeft     : '12px !important',
    zIndex          : dlsZIndex('datePickerPlaceholder'),
    lineHeight      : '15px !important',
    fontSize        : '16px',
    color           : dlsColor('grey4'),
    '&.Mui-focused' : {
      color : dlsColor('grey4')
    }
  },
  fieldLabel : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3')
  },
  requiredAsterisk : {
    color  : dlsColor('aaBlue'),
    margin : '0 2px'
  },
  disabled : {
    '&' : {
      pointerEvents : 'none !important'
    },
    '&:before' : {
      borderBottomStyle : 'solid !important'
    }
  },
  inputRoot : {
    padding   : '4px 11px 4px 6px !important',
    '&:hover' : {
      backgroundColor : dlsAlt('components', 'textfield', 'hoverBackgroundColor')
    },
    '& input' : {
      height      : '35px',
      zIndex      : dlsZIndex('datePickerInputRoot'),
      paddingLeft : ({paddingLeft}: {paddingLeft: string | number}) =>
        paddingLeft ? `${paddingLeft} !important` : '11px'
    }
  },
  underline : {
    backgroundColor : dlsColor('white'),
    '&:after'       : {
      borderColor : dlsColor('caBlue')
    },
    '&:before' : {
      borderColor       : `${dlsColor('grey5')} !important`,
      borderBottomWidth : '1px !important'
    }
  },
  underlineError : {
    backgroundColor : dlsColor('white'),
    '&:after'       : {
      borderColor : `${theme.palette.error.main} !important`
    },
    '&:before' : {
      borderColor       : `${theme.palette.error.main} !important`,
      borderBottomWidth : '1px !important'
    }
  },
  errorText : {
    color : `${theme.palette.error.main}`
  },
  container : {
    position : 'relative',
    minWidth : '185px',
    width    : '47%'
  },
  labelContainer : {
    display        : 'flex',
    justifyContent : 'space-between',
    alignItems     : 'flex-start'
  },
  ctaLink : {
    height   : '20px',
    fontSize : '12px !important'
  },
  alternateInputRoot : {
    borderTop   : `1px solid ${dlsColor('grey5')}`,
    borderRight : `1px solid ${dlsColor('grey5')}`,
    borderLeft  : `1px solid ${dlsColor('grey5')}`,
    padding     : '4px 11px 4px 6px !important',
    '&:hover'   : {
      backgroundColor : dlsAlt('components', 'textfield', 'hoverBackgroundColor')
    },
    '& input' : {
      height      : '35px',
      zIndex      : dlsZIndex('datePickerInputRoot'),
      paddingLeft : ({paddingLeft}: {paddingLeft: string | number}) =>
        paddingLeft ? `${paddingLeft} !important` : '11px'
    }
  },
  icon : {
    display        : 'flex',
    alignSelf      : 'center',
    justifyContent : 'center',
    cursor         : 'pointer'
  }
}));

/**
 * @DatePicker
 *
 * Defines the DatePicker component. It allows us to either choose date, time or choose both simultaneously.
 *
 * ```typescript
 *
 * import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
 *
 * <>
 *    <DatePicker showPicker={showPicker}
 *       // Pass date prop externally. If nothing is passed, current date is selected by default.
 *       value={dateProp}
 *       // Can pass isDate, isTime or isDateTime. If nothing is passed, default will be isDate
 *       isDateTime
 *     />}
 * </>
 *
 * ```
 */
export const DatePicker: FC<DatePickerProps> = ({
  isTime,
  isDateTime,
  toggle,
  fieldLabel,
  placeholder,
  defaultValue,
  hasError,
  errorText,
  required,
  onChange,
  onClose,
  externalClasses,
  overridePopupStyle,
  isNow = false,
  isDate = true,
  isClock = false,
  isToday = false,
  disabled = false,
  manualInput = false,
  isDatePicker = false,
  customError = false,
  useDatePickerTextField = false,
  validDateTimeFormats = ['DD/MM/YYYY h:mm A'],
  validTimeFormats = ['h:mm A'],
  allowManualEntry = false,
  hideClearButton = false,
  alternateInputStyle = false,
  includeContainerStyles = false,
  popoverOffset = 0
}: DatePickerProps) => {
  const errorState                  = {hasError: true, message: translate(`common:editors.scheduleEditor.configs.errors.${customError ? 'dateFormat' : 'format'}`)};
  const validState                  = {hasError: false, message: ''};
  const classes                     = useStyles({fieldLabel, allowManualEntry, overridePopupStyle, popoverOffset});
  const [date, setDate]             = useState<Date>(defaultValue ? new Date(defaultValue) : null);
  const [hoverDay, setHoverDay]     = useState<Date>();
  const [anchorEl, setAnchorEl]     = useState<null | HTMLElement>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [dateTime, setDateTime]     = useState('');
  const [error, setError]           = useState<DatePickerErrorState>(validState);

  useEffect(() => {
    onChangeDate(date);
  }, [date]);

  const updateDateTime = useCallback((value: Date | string, isDateTime: boolean, isTime: boolean): string => {
    const val = value ? new Date(value) : new Date();
    const time = twelveHourTime(val, 'h:mm A');
    return value
      ? isDateTime
        ? `${val.toLocaleDateString()} ${time}`
        : isTime
          ? time
          : val.toLocaleDateString()
      : '';
  }, []);

  const onInputChange = (inputTime: string) => {
    const time = isTime
      ? `${formatIfValid(resolveDateInput(new Date()), VALID_MANUAL_DATE_FORMATS[0])} ${inputTime}`
      : inputTime;
    const toDate       = new Date(time);
    const isEmptyInput = isEmpty(inputTime);
    const format       = !isDatePicker ? validTimeFormats : validDateTimeFormats;
    const valid        = isValidDateTime(inputTime, format) || isEmptyInput;
    const date         = isValid(toDate) && valid ? resolveDateInput(time) : toDate;
    const errorMsg     = format.join(` ${translate('common:conjunctions.or')} `);
    const isInvalid    = !isEmptyInput && (!valid || !isValid(toDate));
    const error        = isInvalid ? (
      isDatePicker ? {hasError: true, message: errorMsg} : errorState
    ) : validState;
    setDateTime(inputTime);
    setError(error);
    onChange?.((!isInvalid && !isEmptyInput ? date : null), isInvalid);
    isValid(toDate) && valid && setDate(date);
  };

  function handleChange({target}: React.ChangeEvent<HTMLInputElement>) {
    manualInput && onInputChange(target?.value);
  }

  function handleClickAway(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(anchorEl ? null : event?.currentTarget);
    setShowPicker(false);
    onClose?.();
  };

  const onDayClick = (day: Date, hours: number, minutes: number) => {
    const d = new Date(day);
    d.setHours(hours);
    d.setMinutes(minutes);
    setHoverDay(d);
    setDate(d);
    onChangeDate(d);
  };

  const onNavigate = (action: number, func: Function) => {
    const d = func(date, action);
    setDate(d);
    onChangeDate(d);
  };

  const onMonthNavigate = (action: number) => {
    onNavigate(action, addMonths);
  };

  const onYearNavigate = (action: number) => {
    onNavigate(action, setYear);
  };

  const onDayHover = (date: Date) => {
    if (!hoverDay || !isSameDay(date, hoverDay)) {
      setHoverDay(date);
    }
  };

  const setTextField = useCallback((event) => {
    setAnchorEl(event?.currentTarget);
    if (!allowManualEntry && !disabled) {
      setShowPicker(useDatePickerTextField);
    }
    toggle?.();
  }, [useDatePickerTextField, allowManualEntry, disabled]);

  const onChangeDate = useCallback((date) => {
    let dateField = updateDateTime(date, isDateTime, isTime);
    if (allowManualEntry) {
      const formatted   = formatIfValid(date, validDateTimeFormats?.[0]);
      const isValidDate = isDatePicker ? isValidDateTime(formatted, validDateTimeFormats)
        : (isValidDateTime(formatted, validTimeFormats) || isEmpty(formatted));
      isValid(formatted) && isValidDate && (dateField = formatted);
    }
    setDateTime(dateField);
    onChange?.(date, false);
    setError(validState);
  }, [validState, allowManualEntry]);

  const onClickNow = useCallback(() => {
    const currentDateTime = resolveDateInput(new Date());
    onChangeDate(currentDateTime);
  }, [onChangeDate]);

  const handlers = {
    onDayClick,
    onDayHover,
    onMonthNavigate,
    onYearNavigate,
    onInputChange,
    onChangeDate
  };

  const datePicker = useMemo(() => (
    <div className={classes.popup}>
      <RenderDatePicker date={date}
        allowManualEntry={allowManualEntry}
        manualInput={dateTime}
        handleClickAway={handleClickAway}
        isDate={isTime ? false : isDate}
        isDateTime={isDateTime}
        isTime={isTime}
        setDate={setDate}
        handlers={handlers}
        hideClearButton={hideClearButton} />
    </div>
  ), [
    date,
    allowManualEntry,
    dateTime,
    isDate,
    isTime,
    isDateTime,
    hideClearButton,
    setDate
  ]);

  return (
    <div className={classnames({
      [classes.container]          : includeContainerStyles,
      [externalClasses?.container] : !!externalClasses?.container
    }
    )}>
      {fieldLabel &&
        <div className={classes.labelContainer}>
          <div className={classes.fieldLabel}>
            {fieldLabel}
            {required && <span className={classes.requiredAsterisk}>*</span>}
          </div>
          {(isNow || isToday) && (
            <TertiaryButton className={classes.ctaLink} onClick={onClickNow}>
              {translate(`common:time.${isNow ? 'now' : 'today'}`)}
            </TertiaryButton>
          )}
        </div>
      }
      {placeholder && !fieldLabel && !dateTime && (
        <InputLabel className={classes.placeholder}
          disableAnimation
          shrink={false}>
          {placeholder}
          {required && !fieldLabel && <span className={classes.requiredAsterisk}>*</span>}
        </InputLabel>
      )}
      <TextField value={(dateTime || '')}
        disabled={disabled}
        onClick={setTextField}
        onChange={handleChange}
        helperText={(hasError && errorText) || (error.hasError && error.message)}
        FormHelperTextProps={{
          className : classes.errorText
        }}
        inputProps={fieldLabel && {placeholder}}
        InputProps={{
          endAdornment : (
            <Box className={classes.icon} onClick={function(event) {
              setAnchorEl(event?.currentTarget);
              setShowPicker(!showPicker);
            }}>
              {alternateInputStyle ? (
                isClock ? <TimeOutlinedIcon /> : <DateOutlinedIcon />
              ) : (
                isClock ? <ClockIcon /> : <NotificationCalendarIcon />
              )}
            </Box>),
          classes : {
            root      : alternateInputStyle ? classes.alternateInputRoot : classes.inputRoot,
            underline : hasError || error.hasError ? classes.underlineError : classes.underline,
            disabled  : classes.disabled
          }
        }} />
      {showPicker && (
        !allowManualEntry ? (
          <Popover anchorEl={anchorEl}
            style={{marginLeft: '10px'}}
            open={showPicker}
            anchorReference='anchorEl'
            anchorOrigin={{
              vertical   : 'bottom',
              horizontal : 'right'
            }}
            transformOrigin={{
              vertical   : 'top',
              horizontal : 'right'
            }}>
            {datePicker}
          </Popover>
        ) : (
          datePicker
        )
      )}
    </div>
  );
};

export default DatePicker;
